// @flow
import React, {useContext} from "react";
import * as PropTypes from "prop-types";
import LIUtils from "../GUI_COMMON/SUPPORT/LIUtils";
import {StoreContext} from "../GUI_COMMON/SUPPORT/store";
import {useCookies} from "react-cookie";
import GuiUtils from "../GUI_COMMON/SUPPORT/GuiUtils";

const MagCard = ({info, onClick, onRemoveCurrentOrNext}) => {
    
    const [globalState] = useContext(StoreContext);
    
    const [cookies,]=useCookies();
    
    function handleOnClick() {
        if(onClick) {
            onClick();
        }
    }
    
    function handleOnRemoveCurrentOrNext(e) {
        e.stopPropagation();
        if(onRemoveCurrentOrNext) {
            onRemoveCurrentOrNext();
        }
    }
    
    function dumpSection(section:Object) {
        if (section) {
            return <div className="d-inline li-font-small">{section.numSamples} 
                <span className="d-inline li-font-tiny ml-1" style={{verticalAlign:"text-bottom"}}>{section.isFull ? "[full]" : ""}</span></div>;
        }
        return null;
    }
    
    function getLastModifiedDate() {
        if(info?.dateModified===null) {
            return "never";
        }
        
        return LIUtils.shortDateFromJsonUtcDateStr(info.dateModified);
    }
    
    function dumpSampleType(sType:string) {
        
        const typeName = GuiUtils.GetSampleTypeNameGivenId(sType, globalState.sampleTypes.items)??"?";
        const sTypeStr = typeName[0];
        const sTypeImage = GuiUtils.GetSampleTypeImageGivenId(sType, globalState.sampleTypes.items);
        
        let extraClass;
        if(isSlideType(sType)) {
            extraClass = "badge-slide";
        }
        else if(isBlockType(sType)) {
            extraClass = "badge-block";
        }
        else {
            extraClass = "badge-general-sample";
        }
        
        return (
            <span className={"badge li-font-large font-weight-bold p-1 px-2 rounded mr-2 "+extraClass} data-toggle="tooltip" data-placement="auto" title={typeName}>
                {!sTypeImage && sTypeStr}
                {sTypeImage && LIUtils.getImgFromImageData(sTypeImage, "", 50, info.id)}
            </span>
        );
    }
    
    function isSlideType(sType) {
        return sType && sType===1;
    }
    function isBlockType(sType) {
        return sType && sType===2;
    }
    
    function calcTotalSamples(sections) {
        return sections.reduce( (acc,section) => acc + section.numSamples, 0);
    }
    
    function calcPercentFull(sections, rows, columns) {
        const totalSamples = calcTotalSamples(sections);
        const totalPossibleSamples = rows*columns;
        return Math.round((totalSamples/totalPossibleSamples)*100);
    }
    
    function dumpSectionPopulations() {
        if(isSlideType(info.holdsSampleType)) {
            return( <table className="table table-sm table-borderless li-font-small">
                        <tbody>
                            <tr><td className="m-0 p-0"><strong>A: </strong>{dumpSection(info.sections[0])}</td></tr>
                            <tr><td className="m-0 p-0"><strong>B: </strong>{dumpSection(info.sections[1])}</td></tr>
                            <tr><td className="m-0 p-0"><strong>C: </strong>{dumpSection(info.sections[2])}</td></tr>
                            <tr><td className="m-0 p-0"><strong>D: </strong>{dumpSection(info.sections[3])}</td></tr>
                        </tbody>
                    </table>
            );
        }
        if(isBlockType(info.holdsSampleType)) {
            return(<>
                <div><strong>A: </strong>{dumpSection(info.sections[0])}</div>
                <div><strong>B: </strong>{dumpSection(info.sections[1])}</div>
            </>);
        }
        
        // general mag
        return(<>
                <div><strong>Total Samples: </strong>{calcTotalSamples(info.sections)}</div>
                <div><strong>% Full: </strong>{calcPercentFull(info.sections, info.rows, info.columns)}</div>
                <div className="mt-2 li-font-small"><strong>Discard Date: </strong>{LIUtils.shortDateFromJsonUtcDateStr(info.canBeDumpedDate)??"-"}</div>
            </>
        );
    }
    
    function isAdminUser() {
        return LIUtils.UserHasLevel(cookies, "ADMIN");
    }

    function dumpIsCurrentIndicator() {
        if(info?.isCurrent!==true) {
            return;
        }
        return <>
             <span className="li-font-small badge badge-success">Is Current</span>
            {isAdminUser() && 
                <button type="button" className="btn fa fa-times-circle li-pointer li-fg-danger ml-1 p-0" 
                        onClick={(e) =>handleOnRemoveCurrentOrNext(e)}/>
            }
            </>;
    }

    function dumpIsNextIndicator() {
        if(info?.isNext!==true) {
            return;
        }
        
        return <>
            <span className="li-font-small badge badge-warning">Is Next</span>
            {isAdminUser() &&
                <button type="button" className="btn fa fa-times-circle li-pointer li-fg-danger ml-1 p-0" 
                    onClick={(e) =>handleOnRemoveCurrentOrNext(e)}/>
            }
        </>;
    }

    function dumpRegisteredTo() {
        if(info.parentDeviceName===null) {
            return <div className="mt-1"/>
        }
        return(
            <div className="li-font-small"><strong className="fa fa-arrow-circle-right"></strong> {info.parentDeviceName??"no device"}</div>
        );
    }

    if(info===null) { // spacer / empty mag card holder
        return <div className="card li-mag-card clear"/>;
    }

    return(
        <div className="card li-mag-card" onClick={handleOnClick}>
            <div className="card-body">
                <div className="row justify-content-between py-1 m-0 h-100">
                    <div>
                        <div className="mb-1">
                            {dumpSampleType(info.holdsSampleType)}
                            <span className="card-title li-font-large font-weight-bold">{info.barcode}</span>
                        </div>
                        {dumpRegisteredTo()}
                        <div className="li-font-tiny mb-2"><strong>Profile:</strong> {info.parentProfileId}</div>
                        {dumpIsCurrentIndicator()}
                        {dumpIsNextIndicator()}
                    </div>
                   
                    <div className="d-flex flex-column text-right li-font-small li-bg-primary-accent p-1 rounded">
                        {dumpSectionPopulations()}
                        <div className="li-flex-spacer"></div>
                        <div className="li-font-tiny li-flex-bottom" style={{minWidth:"110px"}}><strong>Updated:</strong> {getLastModifiedDate()}</div>
                    </div>
                </div>
            </div>
        </div>
    );
}

MagCard.propTypes =  {
    info: PropTypes.object,
    onClick: PropTypes.func,
    onRemoveCurrentOrNext: PropTypes.func
}

export default MagCard;